import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Search, ChevronRight, ArrowUp, ArrowDown, BookOpen, BookOpenCheck, MailPlus } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { useMobile } from "@/hooks/useMobile";
import { memberStage, type MemberStage, type StageRecord } from "@/lib/memberStage";
import {
  NewClientInviteDialog,
  InviteToPortalButton,
  SendResetButton,
} from "@/components/admin/UserAccessActions";
import { PendingInvites } from "@/components/admin/PendingInvites";



type ChipKey = "all" | "stalled" | "low_health" | "active_week";

interface EnrichedUser {
  id: string;
  full_name: string;
  email: string;
  created_at: string;
  una_name: string | null;
  journey_percent: number;
  journey_completed: boolean;
  last_active: string | null;
  health_score: number | null;
  open_questions: number;
  open_tasks: number;
  stalled: boolean;
  is_reviewer: boolean;
  stage: MemberStage;
  in_portal: boolean;
  member_record_id: string | null;
  has_account: boolean;
  dormant: boolean;
}


type SortKey =
  | "created_at"
  | "full_name"
  | "email"
  | "una_name"
  | "journey_percent"
  | "health_score"
  | "last_active"
  | "open_questions"
  | "open_tasks";
type SortDir = "asc" | "desc";

const NUMERIC_OR_DATE: SortKey[] = [
  "created_at",
  "journey_percent",
  "health_score",
  "last_active",
  "open_questions",
  "open_tasks",
];

const TEXT_KEYS: SortKey[] = ["full_name", "email", "una_name"];

const PAGE_SIZES = [25, 50, 100];

export default function AllUsers() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isMobile } = useMobile();
  const [users, setUsers] = useState<EnrichedUser[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [chip, setChip] = useState<ChipKey>("all");
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteRefresh, setInviteRefresh] = useState(0);


  useEffect(() => {
    loadUsers();
  }, []);

  // Reset to page 1 on any control change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, chip, sortKey, sortDir, pageSize]);

  async function loadUsers() {
    setLoading(true);
    try {
      const [
        { data: profiles, error: pErr },
        { data: enrich, error: eErr },
        { data: reviewerRows },
      ] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, full_name, email, created_at")
          .order("created_at", { ascending: false }),
        supabase.rpc("admin_member_list_enrichment"),
        supabase.from("user_roles").select("user_id").eq("role", "reviewer" as any),
      ]);
      // Members whose Association is already with us but who have never had a
      // portal account. They belong on this list so the team can let them in.
      const { data: unlinked } = await (supabase as any)
        .from("member_records")
        .select(
          "id, email, first_name, last_name, legal_full_name, una_name, created_at, fulfillment_stage, origin, record_status",
        )
        .is("user_id", null);
      if (pErr) throw pErr;
      if (eErr) throw eErr;
      const reviewerSet = new Set((reviewerRows || []).map((r: any) => r.user_id));
      const map = (enrich as Record<string, any>) || {};
      const rows: EnrichedUser[] = (profiles || []).map((p: any) => {
        const e = map[p.id] || {};
        const record: StageRecord | null = e.has_member_record
          ? {
              legal_address: e.legal_address,
              una_name: e.una_name,
              una_name_status: e.una_name_status,
              una_name_confirmed_at: e.una_name_confirmed_at,
              mea_status: e.mea_status,
              mea_signed_at: e.mea_signed_at,
              ca_address_option: e.ca_address_option,
              ca_address: e.ca_address,
              suite_number: e.suite_number,
              lease_status: e.lease_status,
              lease_signed_at: e.lease_signed_at,
              payment_status: e.payment_status,
              fulfillment_stage: e.fulfillment_stage,
            }
          : null;
        const journey = {
          percent: e.journey_percent ?? 0,
          completed: e.journey_completed ?? false,
        };
        const stage = memberStage(record, journey);
        const dormant = e.record_status === "dormant";
        return {
          id: p.id,
          full_name:
            e.member_name || p.full_name || "",
          email: p.email || "",
          created_at: p.created_at,
          una_name: e.una_name ?? null,
          journey_percent: journey.percent,
          journey_completed: journey.completed,
          last_active: e.last_active ?? null,
          health_score: e.health_score ?? null,
          open_questions: e.open_questions ?? 0,
          open_tasks: e.open_tasks ?? 0,
          stalled:
            !dormant && (stage.kind === "formation" || stage.kind === "onboarding")
              ? Boolean(e.stalled)
              : false,
          is_reviewer: reviewerSet.has(p.id),
          stage,
          in_portal: stage.kind === "portal",
          member_record_id: e.member_record_id ?? null,
          has_account: true,
          dormant,
        };
      });

      const waitingRows: EnrichedUser[] = ((unlinked || []) as any[])
        .filter(
          (r) => r.fulfillment_stage === "docs_delivered" || r.origin === "legacy_import",
        )
        .map((r) => {
          const stage = memberStage(
            { fulfillment_stage: r.fulfillment_stage } as StageRecord,
            { percent: 0, completed: false },
          );
          return {
            id: `record:${r.id}`,
            full_name:
              [r.first_name, r.last_name].filter(Boolean).join(" ") ||
              r.legal_full_name ||
              "",
            email: r.email || "",
            created_at: r.created_at,
            una_name: r.una_name ?? null,
            journey_percent: 0,
            journey_completed: false,
            last_active: null,
            health_score: null,
            open_questions: 0,
            open_tasks: 0,
            stalled: false,
            is_reviewer: false,
            stage,
            in_portal: false,
            member_record_id: r.id,
            has_account: false,
            dormant: r.record_status === "dormant",
          } as EnrichedUser;
        });

      setUsers([...rows, ...waitingRows]);
    } catch (err: any) {
      console.error("Error loading users:", err);
      toast({
        title: "Could not load users",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  async function toggleReviewer(user: EnrichedUser) {
    try {
      if (user.is_reviewer) {
        const { error } = await supabase
          .from("user_roles")
          .delete()
          .eq("user_id", user.id)
          .eq("role", "reviewer" as any);
        if (error) throw error;
        toast({ title: "Reviewer role removed", description: user.email });
      } else {
        const { error } = await supabase
          .from("user_roles")
          .insert({ user_id: user.id, role: "reviewer" as any });
        if (error) throw error;
        toast({
          title: "Reviewer role assigned",
          description: `${user.email} can now open the reviewer workbench.`,
        });
      }
      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, is_reviewer: !u.is_reviewer } : u,
        ),
      );
    } catch (e: any) {
      toast({
        title: "Could not update role",
        description: e?.message || String(e),
        variant: "destructive",
      });
    }
  }

  const filtered = useMemo(() => {
    let list = users;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (u) =>
          u.email.toLowerCase().includes(q) ||
          u.full_name.toLowerCase().includes(q) ||
          (u.una_name || "").toLowerCase().includes(q),
      );
    }
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    if (chip === "stalled") list = list.filter((u) => u.stalled);
    else if (chip === "low_health")
      list = list.filter((u) => u.health_score !== null && u.health_score < 70);
    else if (chip === "active_week")
      list = list.filter(
        (u) => u.last_active && new Date(u.last_active).getTime() >= weekAgo,
      );
    return list;
  }, [users, searchQuery, chip]);

  const joinedRange = useMemo(() => {
    const dates = users
      .map((u) => (u.created_at ? new Date(u.created_at) : null))
      .filter(
        (d): d is Date => d !== null && !Number.isNaN(d.getTime()) && d.getUTCFullYear() > 2000,
      )
      .sort((a, b) => a.getTime() - b.getTime());
    return { first: dates[0] || null, last: dates[dates.length - 1] || null };
  }, [users]);



  const sorted = useMemo(() => {
    const list = [...filtered];
    const dirMul = sortDir === "asc" ? 1 : -1;

    // Nullability: health_score null and last_active null always sort to bottom
    const isNullish = (u: EnrichedUser): boolean => {
      if (sortKey === "health_score") return u.health_score === null;
      if (sortKey === "last_active") return !u.last_active;
      return false;
    };

    list.sort((a, b) => {
      // Dormant people always sit at the bottom of the roster.
      if (a.dormant !== b.dormant) return a.dormant ? 1 : -1;
      const aNull = isNullish(a);
      const bNull = isNullish(b);
      if (aNull && !bNull) return 1;
      if (!aNull && bNull) return -1;
      if (aNull && bNull) return 0;

      let av: any;
      let bv: any;
      switch (sortKey) {
        case "created_at":
          av = new Date(a.created_at).getTime();
          bv = new Date(b.created_at).getTime();
          break;
        case "last_active":
          av = a.last_active ? new Date(a.last_active).getTime() : 0;
          bv = b.last_active ? new Date(b.last_active).getTime() : 0;
          break;
        case "full_name":
        case "email":
          av = (a[sortKey] || "").toLowerCase();
          bv = (b[sortKey] || "").toLowerCase();
          break;
        case "una_name":
          av = (a.una_name || "").toLowerCase();
          bv = (b.una_name || "").toLowerCase();
          break;
        case "health_score":
          av = a.health_score ?? 0;
          bv = b.health_score ?? 0;
          break;
        case "journey_percent":
        case "open_questions":
        case "open_tasks":
          av = a[sortKey] ?? 0;
          bv = b[sortKey] ?? 0;
          break;
      }
      if (av < bv) return -1 * dirMul;
      if (av > bv) return 1 * dirMul;
      return 0;
    });
    return list;
  }, [filtered, sortKey, sortDir]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIdx = total === 0 ? 0 : (currentPage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, total);
  const paged = useMemo(
    () => sorted.slice(startIdx, endIdx),
    [sorted, startIdx, endIdx],
  );

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(NUMERIC_OR_DATE.includes(key) ? "desc" : "asc");
    }
  }

  function displayName(u: EnrichedUser) {
    return u.full_name?.trim() || u.email || "Member";
  }

  function safeDate(value: string | null | undefined) {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    // A 1970 value means we never had a real date, so we say so plainly.
    if (d.getUTCFullYear() < 2000) return null;
    return d;
  }

  function lastActiveText(u: EnrichedUser) {
    const d = safeDate(u.last_active);
    return d ? formatDistanceToNow(d, { addSuffix: true }) : "Never";
  }

  function openUser(u: EnrichedUser) {
    navigate(u.member_record_id ? `/admin/members/${u.member_record_id}` : `/admin/users/${u.id}`);
  }

  function portalValue(u: EnrichedUser, value: number | null) {
    if (!u.in_portal) return "n/a";
    return value ?? "n/a";
  }


  const chips: { key: ChipKey; label: string }[] = [
    { key: "all", label: "All" },
    { key: "stalled", label: "Stalled in formation" },
    { key: "low_health", label: "Health below 70" },
    { key: "active_week", label: "Active this week" },
  ];

  const RowActions = ({ u }: { u: EnrichedUser }) => (
    <div className="flex items-center justify-end gap-1">
      {u.has_account ? (
        <SendResetButton email={u.email} variant="ghost" label="Send password reset" />
      ) : u.member_record_id && !u.dormant ? (
        <InviteToPortalButton
          memberRecordId={u.member_record_id}
          variant="ghost"
          onSent={() => void loadUsers()}
        />
      ) : null}
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          openUser(u);
        }}
      >
        Open <ChevronRight className="ml-1 h-4 w-4" />
      </Button>
    </div>

  );

  const SortHeader = ({
    label,
    k,
    align = "left",
  }: {
    label: string;
    k: SortKey;
    align?: "left" | "right";
  }) => {
    const active = sortKey === k;
    return (
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${
          active ? "text-foreground font-medium" : ""
        } ${align === "right" ? "justify-end w-full" : ""}`}
      >
        {label}
        {active &&
          (sortDir === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          ))}
      </button>
    );
  };

  const Pagination = () => (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
      <div className="text-xs text-muted-foreground">
        Showing {total === 0 ? 0 : startIdx + 1} to {endIdx} of {total}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Rows per page</span>
        <Select
          value={String(pageSize)}
          onValueChange={(v) => setPageSize(Number(v))}
        >
          <SelectTrigger className="h-8 w-[80px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZES.map((s) => (
              <SelectItem key={s} value={String(s)}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="outline"
          disabled={currentPage <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Previous
        </Button>
        <span className="text-xs text-muted-foreground">
          Page {currentPage} of {totalPages}
        </span>
        <Button
          size="sm"
          variant="outline"
          disabled={currentPage >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        >
          Next
        </Button>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">All Users</h1>
          <p className="text-muted-foreground text-sm">
            Member accounts across the portal
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            Total: {users.length}
          </span>
          <Button size="sm" onClick={() => setInviteOpen(true)}>
            <MailPlus className="h-4 w-4 mr-2" />
            New client invite
          </Button>
        </div>
      </div>

      <NewClientInviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onSent={() => setInviteRefresh((n) => n + 1)}
      />

      <PendingInvites refreshKey={inviteRefresh} />


      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, email, or Association"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <Button
            key={c.key}
            size="sm"
            variant={chip === c.key ? "default" : "outline"}
            onClick={() => setChip(c.key)}
          >
            {c.label}
          </Button>
        ))}
        <div className="ml-auto text-xs text-muted-foreground self-center">
          Showing {total}
        </div>
      </div>

      {isMobile ? (
        <div className="space-y-3">
          {loading ? (
            <p className="text-center py-8 text-muted-foreground">Loading...</p>
          ) : paged.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              No users match these filters.
            </p>
          ) : (
            paged.map((u) => (
              <Card
                key={u.id}
                className={`cursor-pointer ${u.dormant ? "opacity-60" : ""}`}
                onClick={() => openUser(u)}
              >
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium truncate">
                        {displayName(u)}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {u.email}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {u.una_name || "Not named yet"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {u.dormant && <Badge variant="secondary">Dormant</Badge>}
                      {u.stalled && <Badge variant="destructive">Stalled</Badge>}
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!u.has_account}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (u.has_account) toggleReviewer(u);
                        }}
                      >
                        {u.is_reviewer ? (
                          <BookOpenCheck className="h-4 w-4 text-primary" />
                        ) : (
                          <BookOpen className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge variant="outline">
                      {u.stage.label}
                    </Badge>
                    <Badge variant="outline">
                      Health {portalValue(u, u.health_score)}
                    </Badge>
                    <Badge variant="outline">Q {portalValue(u, u.open_questions)}</Badge>
                    <Badge variant="outline">Tasks {portalValue(u, u.open_tasks)}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Last active{" "}
                    {lastActiveText(u)}
                  </p>
                  <div onClick={(e) => e.stopPropagation()}>
                    {u.has_account ? (
                      <SendResetButton email={u.email} />
                    ) : u.member_record_id && !u.dormant ? (
                      <InviteToPortalButton
                        memberRecordId={u.member_record_id}
                        onSent={() => void loadUsers()}
                      />
                    ) : null}
                  </div>
                </CardContent>

              </Card>
            ))
          )}
          <Pagination />
        </div>
      ) : (
        <>
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <SortHeader label="Name" k="full_name" />
                  </TableHead>
                  <TableHead>
                    <SortHeader label="Email" k="email" />
                  </TableHead>
                  <TableHead>
                    <SortHeader label="Association" k="una_name" />
                  </TableHead>
                  <TableHead>
                    <SortHeader label="Journey" k="journey_percent" />
                  </TableHead>
                  <TableHead>
                    <SortHeader label="Health" k="health_score" />
                  </TableHead>
                  <TableHead>
                    <SortHeader label="Last active" k="last_active" />
                  </TableHead>
                  <TableHead className="text-right">
                    <SortHeader label="Questions" k="open_questions" align="right" />
                  </TableHead>
                  <TableHead className="text-right">
                    <SortHeader label="Tasks" k="open_tasks" align="right" />
                  </TableHead>
                  <TableHead className="w-[90px]">Reviewer</TableHead>
                  <TableHead className="w-[100px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : paged.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8">
                      No users match these filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  paged.map((u) => (
                    <TableRow
                      key={u.id}
                      className={`cursor-pointer ${u.dormant ? "opacity-60" : ""}`}
                      onClick={() => openUser(u)}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {displayName(u)}
                          {u.dormant && (
                            <Badge variant="secondary" className="text-[10px]">
                              Dormant
                            </Badge>
                          )}
                          {u.stalled && (
                            <Badge variant="destructive" className="text-[10px]">
                              Stalled
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{u.email}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {u.una_name || (
                          <span className="italic">Not named yet</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {u.stage.label}
                      </TableCell>
                      <TableCell className="text-sm">
                        {portalValue(u, u.health_score)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {lastActiveText(u)}
                      </TableCell>
                      <TableCell className="text-right">
                        {portalValue(u, u.open_questions)}
                      </TableCell>
                      <TableCell className="text-right">
                        {portalValue(u, u.open_tasks)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!u.has_account}
                          title={
                            u.is_reviewer
                              ? "Remove reviewer role"
                              : "Assign reviewer role"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            if (u.has_account) toggleReviewer(u);
                          }}
                        >
                          {u.is_reviewer ? (
                            <BookOpenCheck className="h-4 w-4 text-primary" />
                          ) : (
                            <BookOpen className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <RowActions u={u} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <Pagination />
        </>
      )}

      <p className="text-xs text-muted-foreground">
        Joined dates range from{" "}
        {joinedRange.first ? format(joinedRange.first, "PP") : "n/a"} to{" "}
        {joinedRange.last ? format(joinedRange.last, "PP") : "n/a"}.
      </p>
    </div>
  );
}
